import React, { useState } from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import i18n from "../../../../i18n";
import styles from "./AppointmentDetailsModal.module.css";
import { IAppointment } from "../../types/appointments.types";
import { IAppointment as ILegacyAppointment } from "../../../../types/types";
import { getAppointmentColors } from "../../utils/fundingSourceColors";
import { isOtherEventAppointment } from "../../utils/appointmentOwner";
import TreatmentProgramBadge from "../TreatmentProgramBadge/TreatmentProgramBadge";
import { filterFuturedAppointments } from "../../../../utils/utils";
import { useAppSelector } from "../../../../services/hooks";
import { userSelector } from "../../../../services/selectors/user.selectors";
import Modal from "../../../../components/modals/modal/Modal";
import CancelAppointmentConfirmationPopup from "../../../../components/modals/cancelAppointmentConfirmation_popup/cancelAppointmentConfirmation_popup";
import AlternativeAppointmentPopup from "../../../../components/modals/alternativeAppointmentPopup/alternativeAppointmentPopup";
import {
  useAlternativeAppointmentProgramDateMutation,
  useGetAllAppointmentsInProgramMutation,
  useGetAllOpenTreatmentPlansForUserMutation,
  useLazyGetAppointmentsByUserIdQuery,
} from "../../../../services/api/user.api";
import { TCalendarAppointment } from "../../../../services/types/user.types";
import { useContactAccess } from "../../../../utils/contactAccess";

// Get locale based on current language
const getLocale = () => (i18n.language === "he" ? "he-IL" : "en-US");

interface IAppointmentDetailsModalProps {
  appointment: IAppointment;
  onClose: () => void;
}

/**
 * Appointment Details Modal Component - Client Version (Read-Only)
 *
 * Displays comprehensive appointment information in a read-only format.
 * No editing, cancellation, or meeting start functionality.
 */
const AppointmentDetailsModal: React.FC<IAppointmentDetailsModalProps> = ({
  appointment,
  onClose,
}) => {
  const user = useAppSelector(userSelector);
  // A contact who may only view the calendar gets no reschedule or cancel.
  const { calendarAction } = useContactAccess();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [openAlternativeAppointmentModal, setOpenAlternativeAppointmentModal] =
    useState<TCalendarAppointment | null>(null);

  const [getAllAppointmentProgram] =
    useGetAllOpenTreatmentPlansForUserMutation();
  const [getAllAppointmentsInProgram] =
    useGetAllAppointmentsInProgramMutation();
  const [alternativeAppointment] =
    useAlternativeAppointmentProgramDateMutation();
  const [getallappointments] = useLazyGetAppointmentsByUserIdQuery();

  const isGoogleEvent = appointment.source === 'google_calendar';
  // A device session names no therapist, no group and no medium — it is held on a machine in the
  // clinic — so without the device check it would read as a general note rather than a treatment.
  const isOther = isOtherEventAppointment(appointment, isGoogleEvent);

  const mediaTypes = [
    { id: 1, name: t("MEDIA_CHAT") || "צ'אט" },
    { id: 2, name: t("MEDIA_VIDEO") || "וידאו" },
    { id: 3, name: t("MEDIA_PHONE") || "טלפון" },
    { id: 4, name: t("MEDIA_CLINIC") || "קליניקה" },
  ];

  const getMediaName = (id?: number, name?: string) => {
    if (name) return name;
    if (!id) return "";
    return mediaTypes.find(m => m.id === id)?.name || "";
  };

  // Convert new appointment format to legacy format for compatibility
  const convertToLegacyFormat = (apt: IAppointment): ILegacyAppointment => {
    return {
      appointments: {
        active: true,
        appointmentDate: apt.date,
        appointmentsProgramsId: apt.appointmentsProgramsId || null,
        appointmentsTypeId: null,
        comments: apt.comments,
        companyId: apt.companyId,
        endTime: apt.endTime,
        id: apt.id,
        mediaId: apt.mediaId || 0,
        medias: { id: apt.mediaId || 0, name: apt.mediaName || "" },
        startTime: apt.time,
        statusId: apt.statusId || 1,
        therapistsId: apt.therapistsId || apt.therapistId || 0,
        usersId: apt.usersId || apt.patientId,
        treatmentGroupId: apt.treatmentGroupId,
      },
      avatar: apt.avatar || null,
      therapistName: apt.therapistName,
      groupMembers: null,
    };
  };

  const legacyAppointment = convertToLegacyFormat(appointment);
  const appointmentDate = new Date(appointment.date);

  const getAlternativeAppointment = () => {
    getAllAppointmentProgram({
      companyId: appointment.companyId,
      UsersGuid: user.guid,
      usersName: user.fullName,
    }).then((res) => {
      if ("data" in res) {
        const treatmentPlan = res.data.find(
          (item) =>
            item.appointmentsProgram.id === appointment.appointmentsProgramsId
        );
        treatmentPlan &&
          getAllAppointmentsInProgram(treatmentPlan?.appointmentsProgram).then(
            (res) => {
              if ("data" in res) {
                const allAppointments = res.data;
                treatmentPlan &&
                  alternativeAppointment({
                    ...treatmentPlan?.appointmentsProgram,
                    dateToDelete: appointment.date,
                    deleteType: 2,
                    updateTherapistsUsersId: Number(user.id),
                  })
                    .then((rs) => {
                      if ("data" in rs) {
                        const newDate = rs.data.find(
                          (appointment2) =>
                            !allAppointments.find(
                              (appointment1) =>
                                appointment1.id === appointment2.id
                            )
                        );
                        if (newDate) {
                          setOpenAlternativeAppointmentModal(newDate);
                        }
                      }
                    })
                    .finally(() => {
                      getallappointments();
                    });
              }
            }
          );
      }
    });
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(getLocale(), {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  // Calculate display duration correctly handling midnight crossing
  let displayDuration = appointment.duration;
  if ((!displayDuration || displayDuration <= 0) && appointment.time && appointment.endTime) {
    const startMinutes = parseInt(appointment.time.split(':')[0]) * 60 + parseInt(appointment.time.split(':')[1]);
    const endMinutes = parseInt(appointment.endTime.split(':')[0]) * 60 + parseInt(appointment.endTime.split(':')[1]);
    
    if (endMinutes < startMinutes) {
        // Crosses midnight
        displayDuration = (endMinutes + 24 * 60) - startMinutes;
    } else {
        displayDuration = endMinutes - startMinutes;
    }
  }

  // If cancel modal is open
  if (showCancelModal) {
    return (
      <Modal
        onClose={() => {
          setShowCancelModal(false);
          onClose();
        }}
      >
        <CancelAppointmentConfirmationPopup
          appointmentData={legacyAppointment}
          onClose={() => {
            setShowCancelModal(false);
            onClose();
          }}
        />
      </Modal>
    );
  }

  // If alternative appointment modal is open
  if (openAlternativeAppointmentModal) {
    return (
      <Modal
        onClose={() => {
          setOpenAlternativeAppointmentModal(null);
          onClose();
        }}
      >
        <AlternativeAppointmentPopup
          appointmentData={openAlternativeAppointmentModal}
          onClose={() => {
            setOpenAlternativeAppointmentModal(null);
            onClose();
          }}
        />
      </Modal>
    );
  }

  return (
    <div
      className={styles.modalOverlay}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={styles.modal}>
        {/* Modal Header */}
        <div 
          className={styles.modalHeader}
          style={isGoogleEvent ? { background: 'linear-gradient(135deg, #4285F4 0%, #34a853 100%)' } : (isOther ? { background: 'linear-gradient(135deg, #059669, #10B981)' } : {})}
        >
          <div className={styles.modalTitle}>
            <span>{isGoogleEvent ? "📅" : (isOther ? "📝" : "📋")}</span>
            <span>{isGoogleEvent ? (t("GOOGLE_CALENDAR_EVENT_TITLE") || "אירוע גוגל") : (isOther ? (t("OTHER_EVENT") || "אירוע כללי") : t("APPOINTMENT_DETAILS"))}</span>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div className={styles.modalBody}>
          {/* Treatment Program Badge */}
          {appointment.treatmentProgramName && (
            <div style={{ marginBottom: '20px' }}>
              <TreatmentProgramBadge
                programName={appointment.treatmentProgramName}
                totalSessions={appointment.treatmentProgramTotalSessions}
              />
            </div>
          )}

          {/* Appointment Details Section */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>
              <div className={styles.sectionIcon}>📋</div>
              <span>{t("APPOINTMENT_DETAILS")}</span>
            </div>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>
                  {t("APPOINTMENT_DATE")}
                </div>
                <div className={styles.infoValue}>
                  {formatDate(appointment.date)} | {appointment.time}
                  {appointment.endTime ? ` - ${appointment.endTime}` : ""}
                </div>
              </div>
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>
                  {t("APPOINTMENTS_DURATION")}
                </div>
                <div className={styles.infoValue}>
                  {displayDuration} {t("APPOINTMENTS_MINUTES")}
                </div>
              </div>
              {appointment.treatmentTypeName && (
                <div className={styles.infoItem}>
                  <div className={styles.infoLabel}>
                    {t("APPOINTMENTS_TREATMENT_TYPE")}
                  </div>
                  <div className={styles.infoValue}>
                    {appointment.treatmentTypeName}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Therapist Details Section. A device session has no therapist to name — the machine it
              runs on has its own section below — so this one would only show an empty field. */}
          {!isOther && !isGoogleEvent && !appointment.device && (
          <div className={styles.section}>
            <div className={styles.sectionTitle}>
              <div className={styles.sectionIcon}>👨‍⚕️</div>
              <span>{t("THERAPIST_NAME") || "מטפל"}</span>
            </div>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>{t("NAME") || "שם"}</div>
                <div className={styles.infoValue}>
                  {appointment.therapistName}
                </div>
              </div>
            </div>
          </div>
          )}

          {/* The machine running the session, and where it stands. A device appointment sends the
              patient to a particular room to sit at a particular machine, which is the part of it
              they need; the montage it delivers is clinical and stays with the staff. */}
          {appointment.device?.deviceName && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <div className={styles.sectionIcon}>🔌</div>
                <span>{t("APPOINTMENTS_DEVICE")}</span>
              </div>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <div className={styles.infoLabel}>{t("NAME") || "שם"}</div>
                  <div className={styles.infoValue}>{appointment.device.deviceName}</div>
                </div>
                {appointment.device.treatmentRoomName && (
                  <div className={styles.infoItem}>
                    <div className={styles.infoLabel}>{t("TREATMENT_ROOM")}</div>
                    <div className={styles.infoValue}>
                      🚪 {appointment.device.treatmentRoomName}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Additional Details Section */}
          {(appointment.fundingSourceName || appointment.mediaName || appointment.companyName || appointment.treatmentRoomName) && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <div className={styles.sectionIcon}>💳</div>
                <span>{t("APPOINTMENTS_FUNDING_LOCATION")}</span>
              </div>
              <div className={styles.infoGrid}>
                {appointment.companyName && (
                  <div className={styles.infoItem}>
                    <div className={styles.infoLabel}>{t("COMPANY")}</div>
                    <div className={styles.infoValue}>🏢 {appointment.companyName}</div>
                  </div>
                )}
                {!isOther && (appointment.mediaName || appointment.mediaId) && (
                  <div className={styles.infoItem}>
                    <div className={styles.infoLabel}>{t("APPOINTMENTS_MEDIA")}</div>
                    <div className={styles.infoValue}>📍 {getMediaName(appointment.mediaId, appointment.mediaName)}</div>
                  </div>
                )}
                {appointment.treatmentRoomName && (
                  <div className={styles.infoItem}>
                    <div className={styles.infoLabel}>{t("TREATMENT_ROOM")}</div>
                    <div className={styles.infoValue}>🚪 {appointment.treatmentRoomName}</div>
                  </div>
                )}
                {appointment.fundingSourceName && (
                  <div className={styles.infoItem}>
                    <div className={styles.infoLabel}>{t("APPOINTMENTS_FUNDING")}</div>
                    <div className={styles.infoValue}>
                      <span
                        className={styles.fundingBadge}
                        style={{
                        backgroundColor: getAppointmentColors(appointment).badgeBg,
                        color: getAppointmentColors(appointment).textColor,
                        }}
                      >
                        {appointment.fundingSourceName}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Treatment Group Section */}
          {(appointment.treatmentGroupName || appointment.treatmentGroupId) && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <div className={styles.sectionIcon}>👥</div>
                <span>{t("APPOINTMENTS_TREATMENT_GROUP")}</span>
              </div>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <div className={styles.infoLabel}>{t("GROUP_NAME") || "שם הקבוצה"}</div>
                  <div className={styles.infoValue} style={{ color: "#10B981" }}>
                    {appointment.treatmentGroupName}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          {appointment.comments && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <div className={styles.sectionIcon}>📝</div>
                <span>{isOther ? (t("MESSAGE_CONTENT") || "תוכן") : t("APPOINTMENTS_NOTES")}</span>
              </div>
              <div className={styles.infoValue} style={{ whiteSpace: 'pre-wrap', marginTop: '10px' }}>
                {appointment.comments}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer with Action Buttons */}
        {calendarAction && (
        <div className={styles.modalFooter}>
          {/* Only show reschedule button if appointment is part of a treatment program */}
          {appointment.appointmentsProgramsId && (
            <button
              className={`${styles.actionBtn} ${styles.rescheduleBtn}`}
              onClick={getAlternativeAppointment}
              disabled={appointmentDate <= new Date()}
            >
              {t('RESCHEDULE_APPOINTMENT')}
            </button>
          )}
          <button
            className={`${styles.actionBtn} ${styles.cancelBtn}`}
            onClick={() => setShowCancelModal(true)}
            disabled={!filterFuturedAppointments([legacyAppointment])?.length}
          >
            {t('CANCEL_APPOINTMENT')}
          </button>
        </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentDetailsModal;
